'use client';

import { useEffect, useRef, useState } from 'react';
import type { Brand } from '@/domainLogic/index.ts';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import {
  EVO_CANCELLATION_BODY_PADDING,
  EVO_CANCELLATION_BODY_PADDING_MOBILE,
} from '@/app/components/search/components/Modal/OpenXModal/utils.ts';
import { useIsModalViewport } from '@/app/components/search/utils/useSsrMedia.ts';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import ModalStickyFooter from '@/app/components/search/components/Modal/LayoutElements/ModalStickyFooter';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { shouldShowStrikeThroughPrice } from '@/app/components/hotel-detail/rooms/roomCommon';
import { BottomContinuePanel } from '@/app/components/hotel-detail/BottomContinuePanel';
import { EvoRoomCancellationTypeSelection } from '@/app/components/hotel-detail/evo/rooms/EvoRoomCancellationTypeSelection';
import { useTrackEvoRoomSelection } from '@/app/components/hotel-detail/evo/hooks/useTrackEvoRoomSelection';
import { useEvoGoToNextStepFromRooms } from '@/app/components/hotel-detail/evo/hooks/useEvoContinueFromRooms';

type EvoRoomCancellationTypeSelectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function EvoRoomCancellationTypeSelectionModal({ isOpen, onClose }: EvoRoomCancellationTypeSelectionModalProps) {
  const { t } = useTranslation();
  const { localization, commonData } = usePageWrapperData();
  // The design system turns the dialog into a full-width sheet at MODAL_DESKTOP_BREAKPOINT_PX, so that is
  // the one width this modal should switch on: the body and sticky-footer insets already key off it, and
  // useIsModalViewport is the same constant as a hook (seeded from the UA, so no undefined paint).
  const isSheet = useIsModalViewport(commonData.isMobileDevice);
  const { accommodationOnly } = useUrlSearchParams();
  const { selectedRoom, selectedMealPlan, setSelectedMealPlan } = useSelectedMealPlan();
  const { trackRoomSelection, trackCancellationPopupShown, trackCancellationPopupClicked } = useTrackEvoRoomSelection();
  const goToNextStep = useEvoGoToNextStepFromRooms();

  // Picking a cancellation option only edits this draft; the page-level selection (and the trip overview
  // behind the modal) changes on "Continue" alone, so closing the modal abandons the draft. Re-seeded from
  // the committed selection on every open — and if that selection changes underneath (offers refetch).
  const [draftMealPlan, setDraftMealPlan] = useState<MealPlanType | undefined>(undefined);
  useEffect(() => {
    if (isOpen) setDraftMealPlan(selectedMealPlan);
  }, [isOpen, selectedMealPlan]);

  // POPUP_CANCELLATION_POLICY_SHOWN fires once each time the cancellation-type popup becomes visible. The
  // modal stays mounted while `isOpen` toggles, so the ref gates it to the rising edge (reset on close).
  const shownTrackedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      shownTrackedRef.current = false;
      return;
    }
    if (shownTrackedRef.current || !selectedRoom) return;
    shownTrackedRef.current = true;
    trackCancellationPopupShown(selectedRoom.roomId);
  }, [isOpen, selectedRoom, trackCancellationPopupShown]);

  if (!selectedRoom || !selectedMealPlan) return null;

  // The effect seeding the draft runs only after the first open render.
  const mealPlan = draftMealPlan ?? selectedMealPlan;

  const onContinueClick = () => {
    setSelectedMealPlan(mealPlan);
    // Multiple cancellation options: the room/option events are deferred to here so they carry the
    // cancellation option the user actually chose, rather than the one shown when the modal opened.
    trackRoomSelection(selectedRoom, mealPlan);
    trackCancellationPopupClicked(selectedRoom.roomId, mealPlan.index, mealPlan.dynamicPricingId);
    // The next step is the flight page on a package and checkout on Hotel Only, which has no flights
    // to choose. setSelectedMealPlan above has not reached the provider yet, so pass the draft (MPH-935).
    goToNextStep(selectedRoom, mealPlan);
    onClose();
  };

  return (
    <OpenXModal
      isOpen={isOpen}
      isMobile={isSheet}
      brand={localization.brand as Brand}
      onClose={onClose}
      disableBrowserHistoryNavigation={true}
      fullScreen={false}
      id="evo-cancellation-options-modal"
      aria-label={t('vacation.select.room_modal.title')}
      bodyBackgroundColor="backgroundLayer02"
      size={MODAL_SIZES.L_EVO}
      bodyPadding={EVO_CANCELLATION_BODY_PADDING}
      bodyPaddingS={EVO_CANCELLATION_BODY_PADDING_MOBILE}
      bodyPaddingXS={EVO_CANCELLATION_BODY_PADDING_MOBILE}
      scrollShadows={localization?.configValuesApp?.evolutionEnabled}
      header={{
        sticky: true,
        element: (
          <ModalHeader handleClose={onClose}>
            {t('vacation.select.cc.rooms.cancellation_options.modal_title')}
          </ModalHeader>
        ),
      }}
      footer={{
        sticky: true,
        element: (
          <ModalStickyFooter>
            <BottomContinuePanel
              selectedMealPlan={mealPlan}
              showPrice={true}
              onContinueClick={onContinueClick}
              showStrikeThroughPrice={shouldShowStrikeThroughPrice(mealPlan, accommodationOnly)}
              // Figma 979-40680: the Evo sheet CTA is the 48px-tall (large) button on every breakpoint —
              // MPH-846 caught mobile rendering the 40px one. This modal is Evo-only, so no flag gate.
              buttonSize="large"
              // The 40px horizontal padding belongs to the footer of the *dialog*, so it arrives with the
              // footer's own desktop insets — same `isSheet`, same constant, no 1px window between them.
              wideButton={!isSheet}
            />
          </ModalStickyFooter>
        ),
      }}
    >
      <EvoRoomCancellationTypeSelection
        room={selectedRoom}
        selectedMealPlan={mealPlan}
        anchorMealPlan={selectedMealPlan}
        onSelectMealPlan={setDraftMealPlan}
      />
    </OpenXModal>
  );
}
